import React, { useEffect, useState } from 'react'
import { useFocus, useLayout, useScrollLock } from '../hooks'
import classNames from 'classnames'
import CloseButton from '../close-button'
import Overlay from '../overlay'
import FocusLock from 'react-focus-lock'
import ConditionalWrapper from '../utils/conditional-wrapper'
import { Portal } from '../portal'
import { getLayerClassName } from '../utils/get-layer-class-name'

type Position = 'right' | 'left'

type Props = {
  visible: boolean
  onClose: () => void
  position?: Position
  customBreakpointWidth?: {
    sm?: string
    md?: string
    lg?: string
    xl?: string
  }
  fullWidth?: boolean
  maxWidth?: string
  children: React.ReactNode
  returnOnlyChildren?: boolean
  className?: string
  theme?: string
  usePortal?: boolean
}

const defaultWidths = {
  sm: '90vw',
  md: '75vw',
  lg: '50vw',
  xl: '25vw',
}

export const Drawer = ({
  visible,
  onClose,
  position = 'right',
  customBreakpointWidth = defaultWidths,
  fullWidth = false,
  maxWidth = '1000px',
  children,
  returnOnlyChildren = false,
  className,
  theme,
  usePortal = false,
}: Props) => {
  const { responsive } = useLayout()
  const { htmlElRef, setFocus } = useFocus()
  const [show, setShow] = useState<boolean>(false)
  const transitionDuration = '0.3s'
  const layerClassName = `u-relative ${getLayerClassName('drawer')}`

  const getWidth = () => {
    if (responsive.breakpoints.min.XL) {
      return customBreakpointWidth?.xl ?? defaultWidths.xl
    } else if (responsive.breakpoints.min.LG) {
      return customBreakpointWidth?.lg ?? defaultWidths.lg
    } else if (responsive.breakpoints.min.MD) {
      return customBreakpointWidth?.md ?? defaultWidths.md
    } else {
      return customBreakpointWidth?.sm ?? defaultWidths.sm
    }
  }

  const [width, setWidth] = useState(getWidth())

  useScrollLock(visible)

  useEffect(() => {
    setShow(visible)

    const timeout = setTimeout(() => {
      if (visible) {
        setFocus()
      }
    }, toMS(transitionDuration))

    return () => clearTimeout(timeout)
  }, [visible])

  useEffect(() => {
    if (fullWidth) {
      setWidth('100%')
    } else {
      setWidth(getWidth())
    }
  }, [responsive, fullWidth])

  const toMS = (s: string) => {
    return parseFloat(s) * (/\ds$/.test(s) ? 1000 : 1)
  }

  return returnOnlyChildren ? (
    <>{children}</>
  ) : (
    <ConditionalWrapper
      condition={usePortal}
      wrapper={children => <Portal className={layerClassName}>{children}</Portal>}
      otherwise={children => <div className={layerClassName}>{children}</div>}>
      <Overlay show={show} onClick={() => onClose()} animationDuration={transitionDuration} />
      <FocusLock disabled={!show}>
        <div
          ref={htmlElRef}
          className={classNames('c-drawer', className, {
            'c-drawer--active': show,
            'c-drawer--active--left': position === 'left',
            'c-drawer--active--right': position === 'right',
            'c-drawer--right': position === 'right',
            'c-drawer--left': position === 'left',
          })}
          tabIndex={-1}
          style={{
            width,
            maxWidth,
            transitionDuration,
          }}>
          {children}
          <CloseButton onClose={() => onClose()} theme={theme as any} />
        </div>
      </FocusLock>
    </ConditionalWrapper>
  )
}

export default Drawer
